import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { CartContext } from '../context/CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { getPunditPurohitAuthHeaders, getStoredPunditPurohitUser } from '../utils/auth';
import SubscriptionModal from './SubscriptionModal';
import PoojaAddonsModal from './PoojaAddonsModal';
import AuthModal from './AuthModal';

const formatList = (items = []) => (items.length ? items.join(', ') : 'None selected');

const CartDrawer = () => {
  const {
    cart,
    total,
    isOpen,
    setIsOpen,
    removeFromCart,
    setCart,
  } = useContext(CartContext);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [user, setUser] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [checkoutSettings, setCheckoutSettings] = useState({ paymentGatewayEnabled: false, subscriptionFlowEnabled: false });
  const [subscriptionFlowEnabled, setSubscriptionFlowEnabled] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [contactPhone, setContactPhone] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [bookingReason, setBookingReason] = useState('');
  const [zipStatus, setZipStatus] = useState(null);
  const [addressForm, setAddressForm] = useState({
    type: 'Home',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    isDefault: true,
  });

  const refreshUserAndAddresses = useCallback(() => {
    const savedUser = getStoredPunditPurohitUser();
    setUser(savedUser);

    if (savedUser && isOpen) {
      axios
        .get(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/profile`, {
          headers: getPunditPurohitAuthHeaders(),
        })
        .then((res) => setContactPhone(res.data?.mobile || savedUser.mobile || ''))
        .catch(() => setContactPhone(savedUser.mobile || ''));

      axios
        .get(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/addresses`, {
          headers: getPunditPurohitAuthHeaders(),
        })
        .then((res) => {
          setAddresses(res.data);
          const defaultAddr = res.data.find((addr) => addr.isDefault) || res.data[0] || null;
          setSelectedAddress(defaultAddr);
        })
        .catch((err) => {
          console.error('Unable to load addresses for cart', err);
          setAddresses([]);
          setSelectedAddress(null);
        });
    } else {
      setAddresses([]);
      setSelectedAddress(null);
      setContactPhone('');
    }
  }, [isOpen]);

  useEffect(() => {
    refreshUserAndAddresses();
  }, [refreshUserAndAddresses]);

  useEffect(() => {
    if (cart.length === 0 && editingItem) setEditingItem(null);
  }, [cart.length, editingItem]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/checkout-settings`)
      .then((res) => {
        setCheckoutSettings({
          paymentGatewayEnabled: Boolean(res.data?.paymentGatewayEnabled),
          subscriptionFlowEnabled: Boolean(res.data?.subscriptionFlowEnabled),
        });
        setSubscriptionFlowEnabled(Boolean(res.data?.subscriptionFlowEnabled));
      })
      .catch((err) => {
        console.error('Unable to load checkout settings', err);
        setCheckoutSettings({ paymentGatewayEnabled: false, subscriptionFlowEnabled: false });
        setSubscriptionFlowEnabled(false);
      });
  }, []);

  useEffect(() => {
    if (!subscriptionFlowEnabled || !isOpen || cart.length === 0) return;

    const promptKey = 'pp_checkout_subscription_prompt_seen';
    if (sessionStorage.getItem(promptKey)) return;

    sessionStorage.setItem(promptKey, 'true');
    setShowSubscription(true);
  }, [subscriptionFlowEnabled, isOpen, cart.length]);

  const validateZipCode = async (postalCode) => {
    if (!postalCode.trim()) return null;
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/service-area/validate`, {
        params: { postalCode },
      });
      setZipStatus(res.data);
      return res.data;
    } catch (err) {
      const response = err?.response?.data || { available: false, configured: false, message: 'Unable to verify service coverage.' };
      setZipStatus(response);
      return response;
    }
  };

  const handleAddressSave = async (event) => {
    event.preventDefault();
    if (!user) {
      alert('Please sign in before adding an address.');
      return;
    }

    const coverage = await validateZipCode(addressForm.postalCode);
    if (!coverage?.configured) {
      alert('Service coverage is not configured yet. Please contact the administrator.');
      return;
    }
    if (!coverage.available) {
      alert('Divine Rituals service is not currently available in this ZIP code.');
      return;
    }

    setSavingAddress(true);
    axios
      .post(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/addresses`, addressForm, {
        headers: getPunditPurohitAuthHeaders(),
      })
      .then((res) => {
        const savedAddress = {
          id: res.data.id,
          type: addressForm.type,
          address: addressForm.address,
          city: addressForm.city,
          state: addressForm.state,
          postal_code: addressForm.postalCode,
          isDefault: addressForm.isDefault,
        };

        setAddresses((prev) => {
          const normalized = addressForm.isDefault
            ? prev.map((addr) => ({ ...addr, isDefault: false }))
            : prev;
          return [savedAddress, ...normalized];
        });
        setSelectedAddress(savedAddress);
        setShowAddressForm(false);
        setAddressForm({
          type: 'Home',
          address: '',
          city: '',
          state: '',
          postalCode: '',
          isDefault: true,
        });
      })
      .catch((err) => alert(err?.response?.data?.message || 'Unable to save address.'))
      .finally(() => setSavingAddress(false));
  };

  const isValidPhone = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  };

  const saveBookingPhone = async () => {
    if (!isValidPhone(contactPhone)) {
      alert('Please enter a valid phone number with 10 to 15 digits so the Pandit Ji can coordinate your pooja.');
      return false;
    }
    setSavingPhone(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/booking-phone`,
        { mobile: contactPhone },
        { headers: getPunditPurohitAuthHeaders() }
      );
      return true;
    } catch (error) {
      alert(error?.response?.data?.message || 'Unable to save your phone number.');
      return false;
    } finally {
      setSavingPhone(false);
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!selectedAddress) {
      alert('Please select a service location.');
      return;
    }
    if (!bookingReason.trim()) {
      alert('Please tell us why this pooja is required.');
      return;
    }
    if (!(await saveBookingPhone())) return;

    setCheckoutLoading(true);
    const orderDetails = {
      bookingMode: 'Per Pooja Schedule',
      address: selectedAddress,
      bookingReason: bookingReason.trim(),
      items: cart.map((item) => ({
        id: item.id,
        poojaId: item.poojaId,
        tierId: item.tierId,
        name: item.name,
        type: item.type,
        price: item.price,
        quantity: item.qty || 1,
        bookingDate: item.bookingDate,
        bookingTime: item.bookingTime,
        punditCount: item.included?.pundit_count || item.punditCount || 0,
        itemTotal: item.price * (item.qty || 1),
        requiredItems: item.requiredItems || [],
        punditBringItems: item.punditBringItems || [],
        userArrangedItems: item.userArrangedItems || [],
        needsPrasadam: Boolean(item.needsPrasadam),
        prasadamSelections: item.prasadamSelections || [],
        prasadamTotal: item.prasadamTotal || 0,
        basePrice: item.basePrice || item.price,
        originalBasePrice: item.originalBasePrice || item.basePrice || item.price,
        addonSelections: item.addonSelections || [],
        addonTotal: item.addonTotal || 0,
        paymentGatewayEnabled: item.paymentGatewayEnabled,
        selectedTier: item.selectedTier || null,
      })),
      addons: [],
      finalTotal: total,
      currency: 'USD',
      createdAt: new Date().toISOString(),
    };

    axios
      .post(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/orders`, orderDetails, {
        headers: getPunditPurohitAuthHeaders(),
      })
      .then((res) => {
        if (res.data?.url) {
          window.location.href = res.data.url;
          return;
        }
        if (res.data?.paymentRequired === false) {
          alert(`Booking confirmed. Order number: ${res.data?.order?.orderId || 'created'}`);
          setEditingItem(null);
          setCart([]);
          setIsOpen(false);
          return;
        }
        alert('Unable to start secure checkout.');
      })
      .catch((err) => {
        alert(err?.response?.data?.message || 'Unable to complete booking.');
      })
      .finally(() => setCheckoutLoading(false));
  };

  const isMissingSchedule = cart.some((item) => (
    item.type === 'Pooja' && (!item.bookingDate || !item.bookingTime)
  ));

  const handleCheckoutClick = () => {
    handleCheckout();
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    refreshUserAndAddresses();
  };

  return (
    <>
      {subscriptionFlowEnabled && (
        <SubscriptionModal
          open={showSubscription}
          onClose={() => setShowSubscription(false)}
          title="Upgrade your annual plan"
        />
      )}
      <AuthModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
      <motion.div className="floating-cart-btn" onClick={() => setIsOpen(true)}>
        <span className="cart-icon">Cart</span>
        {cart.length > 0 && <span className="cart-badge">{cart.length}</span>}
      </motion.div>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="drawer-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            <motion.div className="cart-drawer premium" initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}>
              <div className="drawer-header">
                <div>
                  <p className="drawer-eyebrow">Divine Rituals Checkout</p>
                  <h3>Complete your booking <span>({cart.length})</span></h3>
                </div>
                <button className="close-drawer" onClick={() => setIsOpen(false)}>x</button>
              </div>

              <div className="drawer-body">
                {cart.length === 0 ? (
                  <div className="empty-cart-state">
                    <h4>Your cart is empty</h4>
                    <p>Select a pooja to begin your booking.</p>
                    <button className="continue-shopping-btn link-style" onClick={() => setIsOpen(false)}>Continue Shopping</button>
                  </div>
                ) : (
                  <div className="premium-checkout-flow">
                    <section className="checkout-panel">
                      <div className="checkout-section-title">
                        <span>01</span>
                        <div>
                          <h5>Selected poojas</h5>
                          <p>Review every ritual, item choice, and add-on.</p>
                        </div>
                      </div>
                      <div className="cart-items-list">
                        {cart.map((item) => (
                          <div key={item.id} className="drawer-item">
                            {item.image_url && <img src={item.image_url} alt={item.name} />}
                            <div className="item-details">
                              <h4>{item.name}</h4>
                              <div className="cart-item-specs">
                                <small>{item.bookingDate} at {item.bookingTime}</small>
                                {item.selectedTier?.duration && <small>{item.selectedTier.duration}</small>}
                              </div>

                              <div className="cart-item-specs">
                                <small><strong>Pundit brings:</strong> {formatList(item.punditBringItems || [])}</small>
                                <small><strong>User arranges:</strong> {formatList(item.userArrangedItems || [])}</small>
                              </div>

                              {item.needsPrasadam && item.prasadamSelections?.length > 0 && (
                                <div className="cart-item-specs">
                                  <small>
                                    <strong>Prasadam:</strong> {item.prasadamSelections.map((prasadam) => (
                                      `${prasadam.name} x ${prasadam.quantity}`
                                    )).join(', ')}
                                  </small>
                                </div>
                              )}

                              {item.addonSelections?.length > 0 && (
                                <div className="cart-item-specs">
                                  <small>
                                    <strong>Add-ons:</strong> {item.addonSelections.map((addon) => (
                                      `${addon.name} x ${addon.quantity}`
                                    )).join(', ')}
                                  </small>
                                </div>
                              )}

                              <div className="price-qty-row">
                                <div>
                                  {!item.paymentGatewayEnabled && item.originalBasePrice > 0 && (
                                    <span className="struck-price">${item.originalBasePrice}</span>
                                  )}
                                  <p className="item-price">${item.price}</p>
                                </div>
                                <div className="checkout-address-actions">
                                  <button type="button" className="save-btn" onClick={() => setEditingItem(item)}>
                                    Edit Details
                                  </button>
                                  <button type="button" className="cancel-btn" onClick={() => setConfirmDelete(item)}>
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="booking-reason-field">
                        <label htmlFor="booking-reason">Why is this pooja required? <span>*</span></label>
                        <textarea
                          id="booking-reason"
                          required
                          value={bookingReason}
                          onChange={(e) => setBookingReason(e.target.value)}
                          placeholder="For example: housewarming, family blessing, festival, health, or another occasion"
                        />
                      </div>
                    </section>

                    <section className="checkout-panel">
                      <div className="checkout-section-title">
                        <span>02</span>
                        <div>
                          <h5>Service location</h5>
                          <p>Choose where the pundit should arrive.</p>
                        </div>
                      </div>
                      {!user && <div className="guest-checkout-form"><p className="guest-checkout-note">Please sign in or create a UC account before booking. This protects your booking and ensures we can send confirmation and venue updates.</p><button type="button" className="pp-secondary-action" onClick={() => setShowAuthModal(true)}>Sign in / Create account</button></div>}
                      {user && (
                        <>
                          <div className="address-mini-grid premium">
                            {addresses.map((addr) => (
                              <button
                                type="button"
                                key={addr.id}
                                className={`addr-mini-card ${selectedAddress?.id === addr.id ? 'active' : ''}`}
                                onClick={() => setSelectedAddress(addr)}
                              >
                                <div className="radio-dot"></div>
                                <div className="addr-content">
                                  <strong>{addr.type}</strong>
                                  <span>{addr.address}</span>
                                </div>
                              </button>
                            ))}
                          </div>

                          <div className="booking-phone-field">
                            <label htmlFor="pp-booking-phone">Mobile phone for booking <span>*</span></label>
                            <input
                              id="pp-booking-phone"
                              type="tel"
                              inputMode="tel"
                              autoComplete="tel"
                              required
                              value={contactPhone}
                              onChange={(event) => setContactPhone(event.target.value)}
                              placeholder="Example: 470-227-0979"
                            />
                            <small>Required for your booking confirmation and Pandit Ji coordination.</small>
                          </div>

                          {addresses.length === 0 && !showAddressForm && (
                            <div className="checkout-empty-address">
                              <p>No service address saved yet.</p>
                              <button type="button" className="pp-secondary-action compact" onClick={() => setShowAddressForm(true)}>
                                Add Address
                              </button>
                            </div>
                          )}

                          {addresses.length > 0 && !showAddressForm && (
                            <button type="button" className="checkout-add-address-link" onClick={() => setShowAddressForm(true)}>
                              + Add another service address
                            </button>
                          )}

                          {showAddressForm && (
                            <form className="checkout-address-form" onSubmit={handleAddressSave}>
                              <div className="form-row">
                                <div className="input-group">
                                  <label>Label</label>
                                  <select
                                    value={addressForm.type}
                                    onChange={(e) => setAddressForm({ ...addressForm, type: e.target.value })}
                                  >
                                    <option value="Home">Home</option>
                                    <option value="Office">Office</option>
                                    <option value="Temple">Temple</option>
                                    <option value="Other">Other</option>
                                  </select>
                                </div>
                                <div className="input-group">
                                  <label>Postal Code</label>
                                  <input
                                    value={addressForm.postalCode}
                                    inputMode="numeric"
                                    pattern="[0-9]{5}(-[0-9]{4})?"
                                    required
                                    onChange={(e) => { setAddressForm({ ...addressForm, postalCode: e.target.value }); setZipStatus(null); }}
                                    onBlur={(e) => validateZipCode(e.target.value)}
                                  />
                                  {zipStatus && <small className={`zip-status ${zipStatus.available ? 'available' : 'unavailable'}`}>{zipStatus.message || (zipStatus.available ? 'Service is available in this ZIP code.' : 'Service is not available in this ZIP code.')}</small>}
                                </div>
                              </div>
                              <div className="input-group">
                                <label>Full Address</label>
                                <textarea
                                  required
                                  placeholder="Enter complete address details..."
                                  value={addressForm.address}
                                  onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                                />
                                <a className="map-verify-link" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([addressForm.address, addressForm.city, addressForm.state, addressForm.postalCode].filter(Boolean).join(', '))}`} target="_blank" rel="noreferrer">Verify this address on Google Maps ↗</a>
                              </div>
                              <div className="form-row">
                                <div className="input-group">
                                  <label>City</label>
                                  <input
                                    value={addressForm.city}
                                    onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                                  />
                                </div>
                                <div className="input-group">
                                  <label>State</label>
                                  <input
                                    value={addressForm.state}
                                    onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                                  />
                                </div>
                              </div>
                              <label className="auth-checkbox">
                                <input
                                  type="checkbox"
                                  checked={addressForm.isDefault}
                                  onChange={(e) => setAddressForm({ ...addressForm, isDefault: e.target.checked })}
                                />
                                Make default address
                              </label>
                              <div className="checkout-address-actions">
                                <button type="button" className="cancel-btn" onClick={() => setShowAddressForm(false)}>Cancel</button>
                                <button type="submit" className="save-btn" disabled={savingAddress}>
                                  {savingAddress ? 'Saving...' : 'Save and Use Address'}
                                </button>
                              </div>
                            </form>
                          )}
                        </>
                      )}
                    </section>

                    <aside className="checkout-panel checkout-summary-panel">
                      <div className="checkout-section-title">
                        <span>03</span>
                        <div>
                          <h5>Order summary</h5>
                          <p>Confirm your booking total and continue.</p>
                        </div>
                      </div>
                      <div className="checkout-total-card">
                        <div className="total-row"><span>Pooja Total</span><span>${total}</span></div>
                        <div className="total-row grand"><span>Total</span><span>${total}</span></div>
                      </div>
                      {!checkoutSettings.paymentGatewayEnabled && (
                        <div className="cash-to-pandit-note">
                          <strong>Cash to Pandit Ji</strong>
                          <p>Our Pandit Ji won’t ask for any specific Dakshina. Please feel free to offer according to your capabilities.</p>
                        </div>
                      )}
                      <button
                        className="primary-btn checkout-btn"
                        onClick={handleCheckoutClick}
                        disabled={checkoutLoading || savingPhone || isMissingSchedule || Boolean(user && !selectedAddress)}
                      >
                        {!user ? 'Sign In to Checkout'
                          : !selectedAddress ? 'Select Service Location'
                            : isMissingSchedule ? 'Complete Pooja Details'
                                : checkoutLoading ? 'Booking...'
                                : checkoutSettings.paymentGatewayEnabled ? 'Secure Checkout' : 'Confirm Cash Booking'}
                      </button>
                      <button className="continue-shopping-btn link-style" onClick={() => setIsOpen(false)}>
                        Continue Shopping
                      </button>
                    </aside>
                  </div>
                )}
              </div>

              <AnimatePresence>
                {confirmDelete && (
                  <motion.div className="confirm-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <div className="confirm-box">
                      <h4>Remove Item?</h4>
                      <p>Remove <strong>{confirmDelete.name}</strong>?</p>
                      <div className="confirm-actions">
                        <button className="cancel-btn" onClick={() => setConfirmDelete(null)}>No</button>
                        <button className="delete-confirm-btn" onClick={() => { removeFromCart(confirmDelete.id); setConfirmDelete(null); }}>Remove</button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <PoojaAddonsModal
        open={Boolean(editingItem)}
        onClose={() => setEditingItem(null)}
        pooja={editingItem}
        tier={editingItem?.selectedTier}
        images={[editingItem?.image_url].filter(Boolean)}
        editItem={editingItem}
      />
    </>
  );
};

export default CartDrawer;
